"use client";

import Translations from "@translate/components/EmailLogin/steps/CodeVerifyStepDesktop";
import React, { useEffect, useMemo, useRef } from "react";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import type { CodeVerifyStepProps } from "../types";
import { SIGN_IN_PAGE_COPY } from "../signInPageCopy";
import { formatEmailForDisplay } from "../shared/formatEmailForDisplay";
import { CodeInputGroup, focusFirstCodeInput } from "../shared/CodeInputGroup";
import { ResendCodeRow } from "../shared/ResendCodeRow";
import { VerifyCodeButton } from "../shared/VerifyCodeButton";

const I18n = I18nUtil.createScoped(Translations);
const CODE_LENGTH = 6;

export function CodeVerifyStepDesktop({
  email,
  code,
  onCodeChange,
  onVerify,
  verifying = false,
  codeError = false,
  resendCountdown,
  onResend,
  className,
  useV301VerifyChrome = false,
  autoSubmitEnabled = true,
}: CodeVerifyStepProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const prevCodeErrorRef = useRef<boolean>(codeError);
  const displayEmail = useMemo(() => formatEmailForDisplay(email), [email]);
  const titleEmail = useMemo(() => email.trim().toLowerCase(), [email]);
  const fullCode = code.join("");
  const isCodeComplete = fullCode.length === CODE_LENGTH;
  const canVerify = isCodeComplete && !verifying;

  useEffect(() => {
    focusFirstCodeInput(inputRefs, "immediate");
  }, []);

  useEffect(() => {
    const prev = prevCodeErrorRef.current;
    prevCodeErrorRef.current = codeError;
    if (!codeError || prev) {
      return;
    }
    focusFirstCodeInput(inputRefs, "immediate");
  }, [codeError]);

  useEffect(() => {
    if (!isCodeComplete || verifying || autoSubmitEnabled === false) {
      return;
    }
    onVerify("auto");
  }, [isCodeComplete, verifying, autoSubmitEnabled, onVerify]);

  const title = useV301VerifyChrome
    ? SIGN_IN_PAGE_COPY.verifyTitle
    : I18n.t("Check your email");
  const subtitlePrefix = useV301VerifyChrome
    ? SIGN_IN_PAGE_COPY.verifySubtitlePrefix
    : I18n.t("We sent a 6-digit code to");
  const errorMessage = useV301VerifyChrome
    ? SIGN_IN_PAGE_COPY.invalidCode
    : I18n.t("Invalid verification code");

  if (useV301VerifyChrome) {
    return (
      <div
        className={cn("flex w-full flex-col gap-8", className)}
        data-testid="code-verify-step-desktop"
        data-figma-node="96154:99635"
        data-figma-node-pc="96344:111201"
        data-figma-fallback="sign-in-pc-shell+h5-error-content"
      >
        <div
          className="flex shrink-0 flex-col gap-3"
          data-testid="verify-email-title-block"
        >
          <h2
            aria-label="Code Verify Step h2"
            data-openreplay-label="login_codeverifystep_h2"
            id="sem-code-verify-step-h2"
            data-testid="verify-email-title"
            className="headline_regular_m text-content_primary"
          >
            {title}
          </h2>
          <div
            className="flex min-w-0 flex-wrap items-start gap-1"
            title={titleEmail}
            data-testid="verify-email-subtitle-row"
          >
            <p className="body_medium_m text-content_secondary">
              {subtitlePrefix}
            </p>
            <p className="body_medium_m text-content_primary">{displayEmail}</p>
          </div>
        </div>

        <div
          className="flex shrink-0 flex-col gap-4"
          data-testid="verify-email-form-section"
        >
          <div
            className="flex flex-col gap-2"
            data-testid="verify-email-code-error-group"
          >
            <CodeInputGroup
              variant="desktop"
              code={code}
              onCodeChange={onCodeChange}
              codeError={codeError}
              inputRefs={inputRefs}
              useV301Chrome={useV301VerifyChrome}
            />
            {codeError && (
              <p
                className="body_s text-content_accent2_primary"
                role="alert"
                data-testid="verify-email-error-message"
              >
                {errorMessage}
              </p>
            )}
          </div>
          <ResendCodeRow
            resendCountdown={resendCountdown}
            onResend={onResend}
            codeError={codeError}
          />
          <VerifyCodeButton
            variant="desktop"
            canVerify={canVerify}
            verifying={verifying}
            onVerify={onVerify}
            forceEnabledAppearance={useV301VerifyChrome}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("flex w-full flex-col gap-6", className)}
      data-testid="code-verify-step-desktop"
    >
      <div className="flex min-w-0 flex-col gap-3">
        <h2
          aria-label="Code Verify Step h2"
          data-openreplay-label="login_codeverifystep_h2"
          id="sem-code-verify-step-h2"
          className="headline_regular_l text-content_primary"
        >
          {title}
        </h2>
        <p
          className="body_m min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-content_secondary"
          title={titleEmail}
        >
          {subtitlePrefix}
          <span className="body_medium_m ml-1 text-content_primary">
            {displayEmail}
          </span>
        </p>
      </div>
      <div className="flex flex-col">
        <CodeInputGroup
          variant="desktop"
          code={code}
          onCodeChange={onCodeChange}
          codeError={codeError}
          inputRefs={inputRefs}
        />
        {codeError && (
          <p className="body_s mt-2 text-content_accent2_primary" role="alert">
            {errorMessage}
          </p>
        )}
        <ResendCodeRow
          resendCountdown={resendCountdown}
          onResend={onResend}
          codeError={codeError}
          className="mt-4"
        />
        <VerifyCodeButton
          variant="desktop"
          canVerify={canVerify}
          verifying={verifying}
          onVerify={onVerify}
        />
      </div>
    </div>
  );
}
